/**
 * @fileoverview `GET /uk-imports/threshold` — server truth for the UK
 * registration-threshold status strip AND the threshold breakdown page.
 *
 * The status strip used to sum a 500-row-capped client fetch in the
 * browser (`useUkImportLines`, since deleted with the costs table's own
 * 500-cap — docs/features/uk-cbam-threshold-api/
 * phase-4-costs-table-operands.md). Past 500 lines the sum silently
 * understated the total, and this figure decides whether a declarant must
 * register with HMRC within 30 days — so it cannot be a client-side
 * approximation. This endpoint answers over every UK-scoped line, on the
 * server, once.
 *
 * Two hooks, one query key, one fetch: `useUkThreshold` narrows the wire
 * response to the five primitives the status strip renders (unchanged
 * from the previous slice); `useUkThresholdSummary` narrows the SAME
 * response to the window/buckets/months/contributors the threshold
 * breakdown page (`/uk/imports/threshold`) renders. React-query dedupes
 * network requests by query key regardless of `select`, so two mounted
 * hooks reading the same key never double-fetch — and `UK_THRESHOLD_QUERY_KEY`
 * stays the one thing an upload flow invalidates (ImportTable.jsx,
 * UkImportsSearchPage.jsx) to bust both views at once. A fuller single
 * `select` was the other option; two stayed truer to "neither caller gets
 * fields it doesn't render."
 *
 * Every date arrives ISO UTC and is parsed to dayjs here, once, so no
 * consumer of either hook ever re-parses a bare string.
 */

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import http from 'helpers/requests';

dayjs.extend(utc);

export const UK_THRESHOLD_ENDPOINT = '/uk-imports/threshold';

/**
 * Exported so an import flow can invalidate it. An upload changes the
 * threshold, and this query is the only thing that knows the new answer.
 */
export const UK_THRESHOLD_QUERY_KEY = 'uk-imports-threshold';

/**
 * Bust every threshold view at once: the summary (strip + breakdown header)
 * AND the breakdown table's pages, whose key is `[UK_THRESHOLD_QUERY_KEY,
 * 'lines', …]` (useUkThresholdLines.js) so this one prefix reaches both.
 *
 * Call it after ANY write that can move a line in or out of the rolling
 * window or the 30-day forward test — create, edit (a changed clearance date
 * or value), delete, bulk upload, purchase create/promote. Only the upload
 * flows used to, so a manual date edit left the old total on screen for up to
 * `staleTime`. A no-op for an EU declarant: nothing is cached under the key.
 */
export const invalidateUkThreshold = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: [UK_THRESHOLD_QUERY_KEY] });

const fetchUkThreshold = async () => {
  const response = await http.get(UK_THRESHOLD_ENDPOINT);
  return response?.data ?? null;
};

const toUtcOrNull = (iso) => (iso ? dayjs.utc(iso) : null);

// `outlook: null` (stays over the horizon) is a different fact from
// `alreadyBelow: true` (already settled) — kept as a tri-state, never
// collapsed into a boolean or a sentinel date. Shared by both selects
// below since both hand `outlook` to their caller unchanged.
const selectOutlook = (outlook) =>
  outlook ? { ...outlook, checkDate: toUtcOrNull(outlook.checkDate) } : null;

// The 30-day forward test (purchases PU19): imports clearing in the next 30
// days plus unlinked purchases due by then. `reachesThreshold` is the SERVER's
// comparison on the exact total — never re-derive it from the rounded `total`
// (a £49,999.996 total rounds to £50,000 and is still below). `null` when the
// backend predates the block, so an older deploy renders nothing rather than
// a false "£0 · below".
const tallyOrZero = (tally) => ({ count: tally?.count ?? 0, total: tally?.total ?? 0 });
const selectForward = (forward) =>
  forward
    ? {
        windowStart: toUtcOrNull(forward.windowStart),
        windowEnd: toUtcOrNull(forward.windowEnd),
        days: forward.days ?? 30,
        total: forward.total ?? 0,
        reachesThreshold: Boolean(forward.reachesThreshold),
        imports: tallyOrZero(forward.imports),
        purchases: {
          ...tallyOrZero(forward.purchases),
          overdue: tallyOrZero(forward.purchases?.overdue),
        },
        excluded: tallyOrZero(forward.excluded),
      }
    : null;

// The registration verdict (docs/features/uk-registration-verdict R1/R3): the
// server's one answer, the test that triggered it, and the register-by date it
// computed from the data. Every date parsed once, here. `null` when the backend
// predates the block — the strip says it cannot answer rather than guessing one
// from a rounded £ (R2).
const selectTestTrigger = (trigger) => ({
  reachesThreshold: Boolean(trigger?.reachesThreshold),
  triggerDate: toUtcOrNull(trigger?.triggerDate),
  registerBy: toUtcOrNull(trigger?.registerBy),
  upcoming: Boolean(trigger?.upcoming),
  sinceBeforeHorizon: Boolean(trigger?.sinceBeforeHorizon),
});
export const selectRegistration = (registration) =>
  registration
    ? {
        status: registration.status,
        triggeredBy: registration.triggeredBy ?? null,
        registerBy: toUtcOrNull(registration.registerBy),
        // Set when the deadline cannot be dated (the run is older than the
        // server's scan): `registerBy` is then null and the verdict is overdue.
        registerByUnknown: registration.registerByUnknown ?? null,
        overdue: Boolean(registration.overdue),
        backward: selectTestTrigger(registration.backward),
        forward: selectTestTrigger(registration.forward),
        record: registration.record
          ? {
              registeredAt: toUtcOrNull(registration.record.registeredAt),
              reference: registration.record.reference ?? null,
              recordedAt: toUtcOrNull(registration.record.recordedAt),
            }
          : null,
      }
    : null;

/**
 * What the registration verdict is worded from (utils/registrationVerdictView
 * `buildVerdictView`). ONE selection, shared by both hooks below: the strip
 * spreads it into its flat `threshold`, the breakdown page reads it as
 * `summary.verdictInput` — so the two surfaces cannot word the same response
 * from differently-mapped fields.
 */
export const selectVerdictInput = (raw) => ({
  checkDate: toUtcOrNull(raw.window?.checkDate),
  windowEnd: toUtcOrNull(raw.window?.windowEnd),
  hasImports: Boolean(raw.hasImports),
  total: raw.counted?.total ?? 0,
  outlook: selectOutlook(raw.outlook),
  forward: selectForward(raw.forward),
  registration: selectRegistration(raw.registration),
  skippedLines: raw.skippedLines ?? 0,
  // UK CBAM's commencement: nothing cleared before it counts (R14).
  commencesOn: toUtcOrNull(raw.threshold?.commencesOn),
  // Before commencement only: both totals as if UK CBAM already applied (R16) —
  // a heads-up, never a verdict. `reachesThreshold` is the server's exact compare.
  indicative: raw.indicative
    ? {
        counted: {
          total: raw.indicative.counted?.total ?? 0,
          reachesThreshold: Boolean(raw.indicative.counted?.reachesThreshold),
        },
        forward: {
          total: raw.indicative.forward?.total ?? 0,
          reachesThreshold: Boolean(raw.indicative.forward?.reachesThreshold),
        },
      }
    : null,
});

const useUkThresholdQuery = (select) =>
  useQuery({
    queryKey: [UK_THRESHOLD_QUERY_KEY],
    queryFn: fetchUkThreshold,
    select,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

/**
 * @returns {{
 *   threshold: {
 *     checkDate: dayjs.Dayjs|null,
 *     thresholdValueGbp: number,
 *     hasImports: boolean,
 *     total: number,
 *     lineCount: number,
 *     actualCount: number,
 *     defaultCount: number,
 *     outlook: { checkDate: dayjs.Dayjs, total: number, alreadyBelow: boolean }|null,
 *     forward: { windowStart, windowEnd, days, total, reachesThreshold,
 *                imports, purchases: {count,total,overdue}, excluded }|null,
 *     skippedLines: number,
 *   }|null,
 *   isLoading: boolean,
 *   isFetching: boolean,
 *   isError: boolean,
 *   refetch: Function,
 * }}
 */
export function useUkThreshold() {
  const {
    data: threshold,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useUkThresholdQuery((raw) => {
    if (!raw) return null;
    return {
      // checkDate, windowEnd, hasImports, total, outlook, forward,
      // registration, skippedLines — the verdict's input, flat.
      ...selectVerdictInput(raw),
      nextCheckDate: toUtcOrNull(raw.window?.nextCheckDate),
      thresholdValueGbp: raw.threshold?.valueGbp ?? 0,
      lineCount: raw.counted?.lineCount ?? 0,
      actualCount: raw.counted?.actualCount ?? 0,
      defaultCount: raw.counted?.defaultCount ?? 0,
      // The server's exact comparison; `total` is rounded (R2).
      countedReachesThreshold: Boolean(raw.counted?.reachesThreshold),
    };
  });

  return { threshold: threshold ?? null, isLoading, isFetching, isError, refetch };
}

const ZERO_BUCKET = { count: 0, total: 0 };

/**
 * The rest of the same wire response, for the threshold breakdown page:
 * the window bounds, per-bucket counts/totals, 12 pre-aggregated months
 * and the top contributors — everything `useUkThreshold` narrows away
 * above. `months[].monthStart` is added here (never on the wire) so it
 * arrives pre-parsed like every other date, and `contributors.top[].id`
 * mirrors `lineId` so a contribution block and a table row share one
 * identity field (`useUkThresholdLines.js` does the same for table rows).
 *
 * @returns {{
 *   summary: {
 *     window: { checkDate, windowStart, windowEnd, nextCheckDate }, // dayjs
 *     threshold: { valueGbp: number, currency: string },
 *     hasImports: boolean,
 *     buckets: { counted, notYetCounted, agedOut, excluded }, // each {count,total}
 *     months: Array<{ key, total, actualTotal, defaultTotal, count, monthStart }>,
 *     contributors: { top: Array<{id,lineId,supplierId,valueGbp}>, other: {count,total}|null },
 *     outlook: {...}|null,
 *     forward: {...}|null, // see useUkThreshold
 *     registration: {...}|null,
 *     skippedLines: number,
 *     verdictInput: {...}, // see selectVerdictInput
 *   }|null,
 *   isLoading: boolean,
 *   isFetching: boolean,
 *   isError: boolean,
 *   refetch: Function,
 * }}
 */
export function useUkThresholdSummary() {
  const {
    data: summary,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useUkThresholdQuery((raw) => {
    if (!raw) return null;
    return {
      window: {
        checkDate: toUtcOrNull(raw.window?.checkDate),
        windowStart: toUtcOrNull(raw.window?.windowStart),
        windowEnd: toUtcOrNull(raw.window?.windowEnd),
        nextCheckDate: toUtcOrNull(raw.window?.nextCheckDate),
      },
      threshold: {
        valueGbp: raw.threshold?.valueGbp ?? 0,
        currency: raw.threshold?.currency ?? 'GBP',
      },
      hasImports: Boolean(raw.hasImports),
      buckets: {
        counted: raw.buckets?.counted ?? ZERO_BUCKET,
        notYetCounted: raw.buckets?.notYetCounted ?? ZERO_BUCKET,
        agedOut: raw.buckets?.agedOut ?? ZERO_BUCKET,
        excluded: raw.buckets?.excluded ?? ZERO_BUCKET,
      },
      months: (raw.months ?? []).map((m) => ({ ...m, monthStart: dayjs.utc(`${m.key}-01`) })),
      contributors: {
        top: (raw.contributors?.top ?? []).map((c) => ({ ...c, id: c.lineId })),
        other: raw.contributors?.other ?? null,
      },
      outlook: selectOutlook(raw.outlook),
      forward: selectForward(raw.forward),
      registration: selectRegistration(raw.registration),
      skippedLines: raw.skippedLines ?? 0,
      // The same selection the strip words its verdict from.
      verdictInput: selectVerdictInput(raw),
    };
  });

  return { summary: summary ?? null, isLoading, isFetching, isError, refetch };
}

export default useUkThreshold;
